import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import classnames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';

import BackButton from '../../../components/shared/BackButton/BackButton';
import { IMPORTED_FROM } from '../../../constants/commonConstants';
import { normalizeURL } from '../../../helpers/commonHelper';
import { IsMobile } from '../../../helpers/IsMobile';
import { useIsIOS } from '../../../hooks/useDeviceDetection';
import useReferralProviderData from '../../../hooks/useReferralProviderData';
import { saveCandidateInfo } from '../../../redux/actions/referRequestActions';
import { useGetReferDataQuery } from '../../../redux/apis/referralApi';
import urls from '../../../routing/urls';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import OpeningDescription from '../OpeningComponents/OpeningDescription/OpeningDescription';
import OpeningJobHeader from '../OpeningComponents/OpeningJobHeader/OpeningJobHeader';
import ReferralRewardBadge from '../OpeningComponents/ReferralRewardBadge/ReferralRewardBadge';
import RequiredEducation from '../OpeningComponents/RequiredEducation/RequiredEducation';
import SmartRewardBadge from '../OpeningComponents/SmartRewardBadge/SmartRewardBadge';
import ButtonMenu from '../OpeningComponents/ShareButtonMenu/ButtonMenu';

import styles from './OpeningDetail.module.scss';

const OpeningContent = ({ openingDetails, isJobPreview, isPublic }) => {
  const isMobile = IsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const openingRef = useRef(null);
  const user = useSelector(selectCurrentUser);
  const organization = useSelector(selectCurrentOrganization);
  const isMobileRoute =
    location.pathname.includes('/jobs/details') || (location.pathname.includes('/job/details') && isMobile);

  const {
    title,
    description,
    organization_name,
    org_identifier,
    skills,
    job_location,
    candidate_status,
    job_education_name,
    logo_image_url,
    industries,
    employer_profile_link,
    published_date,
    min_salary,
    max_salary,
    referral_provider_details,
    avatar_thumb,
    eligible_for_rewards,
    work_place_type,
    category,
    work_type,
    job_experience_name,
    job_urgency_type_name,
    show_public_rewards,
    external_tcs,
    monetary_reward_sum,
    currency_id,
    imported_from,
    visibility,
    reward_visibility,
    can_refer,
  } = openingDetails;

  const referralRewardDetails = openingDetails?.reward_templates_attributes;
  const isCandidateApplied = candidate_status?.applied;
  const [isSticky, setIsSticky] = useState(false);
  const [descriptionOffset, setDescriptionOffset] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [searchParams] = useSearchParams();
  const referralToken = searchParams.get('refer_token');
  const { data: referData } = useGetReferDataQuery(referralToken, { skip: !referralToken });
  const referralProvider = referData?.payload?.referrer || null;
  const isIOSDevice = useIsIOS();
  const { referralProviderData } = useReferralProviderData();
  const referralProviderSlug = searchParams.get('rp') || referralProviderData?.user?.slug || undefined;
  const calculateHeight = () => {
    if (openingRef.current) {
      const viewportHeight = window.innerHeight;

      const openingContainer = document.querySelector('.opening-container');
      const openingContainerOffsetTop = openingContainer.getBoundingClientRect().top;

      let remainingHeight = viewportHeight - openingContainerOffsetTop;
      if (isMobile) {
        remainingHeight -= 60;
      }
      openingContainer.style.height = `${remainingHeight}px`;
    }
  };

  // For any sharing scope other than "everyone"/"marketplace_only" the refer button is
  // only shown to a logged-in user the backend confirms is allowed to refer (a member of
  // the job's team, or of a group named in the sharing scope). `can_refer` encapsulates
  // that check; fall back to the visibility heuristic if the flag is absent.
  // Visitors who arrived via a job share / my-referral link (`rp`) can only Apply —
  // including when the job has an external apply link.
  const publicVisibility = visibility?.includes('everyone') || visibility?.includes('marketplace_only');
  const arrivedViaReferralProviderLink = Boolean(referralProviderSlug);
  const disableReferButton =
    arrivedViaReferralProviderLink || (can_refer === undefined ? !publicVisibility && !user : !can_refer);

  useEffect(() => {
    calculateHeight();
  }, [openingRef.current]);

  useEffect(() => {
    calculateHeight();
  }, [window.innerHeight]);

  const redirectToOrg = useCallback(() => {
    navigate(isPublic ? `/company/${org_identifier}` : `/jobs/company/${org_identifier}`);
  }, [employer_profile_link]);

  const referralRewardInstallments = referralRewardDetails
    ? referralRewardDetails.map((detail) => detail.reward_template_installments_attributes)
    : [];

  const handleApplyButtonClick = useCallback(() => {
    window.open(employer_profile_link, '_self');
  }, [employer_profile_link]);

  const handleScroll = () => {
    const { scrollTop } = openingRef.current;
    if (openingRef.current) {
      setIsSticky(scrollTop >= descriptionOffset - 5);
    }
  };

  const handleApplyJob = () => {
    if (openingDetails?.external_job_link) {
      if (user) {
        const data = {
          firstName: user.first_name,
          lastName: user.last_name,
          phone_number: user.phone_number,
          email: user.email,
          job_id: openingDetails.id,
          full_name: `${user.first_name} ${user.last_name}`,
          referrer_id: referralProvider?.id,
          from_id: referralProviderData?.user?.id ? referralProviderData?.user?.id : user.id,
        };
        dispatch(
          saveCandidateInfo(data, () => {
            if (openingDetails?.external_job_link) {
              if (isIOSDevice) {
                const link = openingDetails.external_job_link;
                const url = normalizeURL(link);
                window.location.href = url;
              } else {
                const link = openingDetails.external_job_link;
                const url = normalizeURL(link);
                window.open(url, '_blank');
              }
            }
          })
        );
        return;
      }
      setShowModal(true);
      return;
    }

    const url = location.pathname.includes('company') || isPublic ? urls.companyApplicationFlow : urls.applicationFlow;
    const baseApplyPath = url.replace(
      ':companyName',
      organization?.general_setting_identifier || openingDetails.general_setting_identifier
    );
    const applyQuery = new URLSearchParams({ apply_for: openingDetails.uuid });
    if (referralProviderSlug) {
      applyQuery.set('rp', referralProviderSlug);
    }
    navigate(`${baseApplyPath}?${applyQuery.toString()}`);
  };

  const renderRewardBadge = () => {
    if (reward_visibility && reward_visibility.visibility !== 'none') {
      if (reward_visibility.visibility === 'full') {
        return !isEmpty(referralRewardInstallments) ? (
          <ReferralRewardBadge
            rewards={referralRewardInstallments}
            eligibileForRewards={eligible_for_rewards}
            monetaryRewardSum={monetary_reward_sum}
            user={user}
            showPublicRewards={show_public_rewards}
          />
        ) : null;
      }
      return <SmartRewardBadge rewardVisibility={reward_visibility} />;
    }

    if (!reward_visibility && !isEmpty(referralRewardInstallments) && (show_public_rewards || !!user)) {
      return (
        <ReferralRewardBadge
          rewards={referralRewardInstallments}
          eligibileForRewards={eligible_for_rewards}
          monetaryRewardSum={monetary_reward_sum}
          user={user}
          showPublicRewards={show_public_rewards}
        />
      );
    }

    return null;
  };

  return (
    <div
      className={classnames(styles.rootContainer, { [styles.jobPreviewBorder]: isJobPreview }, 'opening-container')}
      ref={openingRef}
      onScroll={handleScroll}
    >
      {isMobileRoute && !isJobPreview && <BackButton isArrowButton />}

      <OpeningJobHeader
        title={title}
        oragnizationName={organization_name}
        logo={logo_image_url}
        location={job_location}
        referButtonOnClick={() => {}}
        applied={candidate_status?.applied || false}
        redirectToOrg={redirectToOrg}
        publishedDate={published_date}
        minSalary={min_salary}
        maxSalary={max_salary}
        category={category}
        currency_id={currency_id}
        work_place_type={work_place_type}
        work_type={work_type}
        job_experience_name={job_experience_name}
        job_urgency_type_name={job_urgency_type_name}
      />

      <RequiredEducation
        skills={skills}
        education={job_education_name}
        experienceLevel={job_experience_name}
        industries={industries}
      />

      {renderRewardBadge()}

      <ButtonMenu
        isCandidateApplied={isCandidateApplied}
        handleApplyButtonClick={handleApplyButtonClick}
        opening={openingDetails}
        handleApplyJob={handleApplyJob}
        currentUser={user}
        showModal={showModal}
        setShowModal={setShowModal}
        isExternalLink={openingDetails?.external_job_link}
        isPublic={isPublic}
        disableReferButton={disableReferButton}
        referralProvider={referralProvider}
      />

      <OpeningDescription
        skills={skills}
        description={
          !isEmpty(description) && imported_from === IMPORTED_FROM.DAYFORCE
            ? description.replaceAll('\n', '<br>')
            : description
        }
        referralProviderDetails={referral_provider_details}
        avatarThumb={avatar_thumb}
        referralRewardDetails={referralRewardInstallments}
        eligibileForRewards={eligible_for_rewards}
        isJobPreview={isJobPreview}
        showPublicRewards={show_public_rewards}
        isLoggedInUser={!!user}
        isSticky={isSticky}
        setDescriptionOffset={setDescriptionOffset}
        externalTcs={external_tcs}
        jobId={openingDetails.id}
        opening={openingDetails}
        rewardVisibility={reward_visibility}
      />
    </div>
  );
};

export default OpeningContent;
